import { useState } from "react";
import api from "@/lib/api";
import { Loader2, Calculator, BarChart3 } from "lucide-react";
import { toast } from "sonner";
import { handleQuotaError } from "@/lib/quota";
import { useNavigate } from "react-router-dom";

export default function AnalysisPanel({ accent }) {
  const nav = useNavigate();
  const [tab, setTab] = useState("dcf");
  const [dcfIn, setDcfIn] = useState({
    free_cash_flow: 1000, growth_rate_high: 0.08, growth_years: 5,
    terminal_growth: 0.025, discount_rate: 0.09, net_debt: 0, shares_outstanding: 100,
  });
  const [dcfOut, setDcfOut] = useState(null);
  const [ratioIn, setRatioIn] = useState({
    revenue: 10000, cogs: 6000, opex: 2000, interest: 200, tax: 400,
    total_assets: 15000, total_equity: 8000, total_debt: 4000,
    current_assets: 5000, current_liabilities: 2500, inventory: 1500,
    cash: 1000, shares_outstanding: 100, market_price: 60,
  });
  const [ratioOut, setRatioOut] = useState(null);
  const [busy, setBusy] = useState(false);

  const runDcf = async () => {
    setBusy(true);
    try {
      const r = await api.post("/falcon/analysis/dcf", dcfIn);
      setDcfOut(r.data);
    } catch (e) {
      if (!handleQuotaError(e, nav)) toast.error(e?.response?.data?.detail || "DCF failed");
    } finally { setBusy(false); }
  };
  const runRatios = async () => {
    setBusy(true);
    try {
      const r = await api.post("/falcon/analysis/ratios", ratioIn);
      setRatioOut(r.data);
    } catch (e) {
      if (!handleQuotaError(e, nav)) toast.error(e?.response?.data?.detail || "Ratios failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="falcon-analysis">
      <div>
        <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Fundamental analysis</div>
        <h2 className="font-serif text-3xl mt-1 flex items-center gap-2"><Calculator className="h-6 w-6" /> Valuation toolbox</h2>
        <div className="text-xs text-muted-foreground mt-1">DCF, ratio dashboards, and peer comparison — deterministic Python.</div>
      </div>

      <div className="flex gap-2">
        {[["dcf", "DCF model"], ["ratios", "Financial ratios"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`analysis-tab-${k}`}
            className={`h-9 px-3 text-xs rounded-sm border transition-colors ${tab === k ? "border-foreground bg-card" : "border-border/60 bg-card/40 text-muted-foreground hover:text-foreground"}`}>
            {l}
          </button>
        ))}
      </div>

      {tab === "dcf" && (
        <div className="grid lg:grid-cols-2 gap-4">
          <div className="rounded-sm border border-border/60 bg-card p-4 space-y-3">
            <div className="text-sm font-medium">Assumptions</div>
            {[
              ["free_cash_flow", "Year-0 FCF ($M)", 1],
              ["growth_rate_high", "Growth rate (decimal, e.g. 0.08)", 0.01],
              ["growth_years", "Years of high growth", 1],
              ["terminal_growth", "Terminal growth rate", 0.005],
              ["discount_rate", "Discount rate (WACC)", 0.005],
              ["net_debt", "Net debt ($M)", 1],
              ["shares_outstanding", "Shares outstanding (M)", 1],
            ].map(([k, l, step]) => (
              <div key={k}>
                <label className="block text-[10px] uppercase tracking-wider text-muted-foreground mb-1">{l}</label>
                <input type="number" step={step} value={dcfIn[k]} onChange={(e) => setDcfIn({ ...dcfIn, [k]: parseFloat(e.target.value) || 0 })}
                  data-testid={`dcf-${k}`}
                  className="h-8 w-full rounded-sm border border-border bg-background px-2 text-sm font-mono" />
              </div>
            ))}
            <button onClick={runDcf} disabled={busy} data-testid="dcf-run"
              className="w-full h-9 text-sm rounded-sm text-white disabled:opacity-60 inline-flex items-center justify-center gap-2"
              style={{ background: accent }}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <BarChart3 className="h-4 w-4" />}
              Compute fair value
            </button>
          </div>
          <div className="rounded-sm border border-border/60 bg-card p-4">
            <div className="text-sm font-medium mb-3">Result</div>
            {dcfOut ? (
              <div className="space-y-3">
                <div className="text-3xl font-serif" data-testid="dcf-per-share">
                  ${dcfOut.per_share_value.toLocaleString(undefined, { maximumFractionDigits: 2 })}<span className="text-sm text-muted-foreground"> / share</span>
                </div>
                <div className="text-xs text-muted-foreground">
                  Equity value <span className="font-mono text-foreground">${dcfOut.equity_value.toLocaleString()}</span> ·
                  Enterprise value <span className="font-mono text-foreground">${dcfOut.enterprise_value.toLocaleString()}</span>
                </div>
                <div className="text-xs text-muted-foreground">
                  Terminal value <span className="font-mono text-foreground">${dcfOut.terminal_value.toLocaleString()}</span>
                  (PV: ${dcfOut.terminal_value_pv.toLocaleString()})
                </div>
                <table className="w-full text-xs mt-4">
                  <thead><tr className="text-muted-foreground uppercase tracking-wider">
                    <th className="text-start font-medium">Yr</th>
                    <th className="text-start font-medium">FCF</th>
                    <th className="text-start font-medium">DF</th>
                    <th className="text-start font-medium">PV</th>
                  </tr></thead>
                  <tbody>
                    {dcfOut.projections.map((p) => (
                      <tr key={p.year} className="border-t border-border/40">
                        <td className="py-1">{p.year}</td>
                        <td className="py-1 font-mono">{p.projected_fcf.toLocaleString()}</td>
                        <td className="py-1 font-mono">{p.discount_factor}</td>
                        <td className="py-1 font-mono">{p.discounted_fcf.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-sm text-muted-foreground">Enter assumptions and press Compute.</div>
            )}
          </div>
        </div>
      )}

      {tab === "ratios" && (
        <div className="grid lg:grid-cols-2 gap-4">
          <div className="rounded-sm border border-border/60 bg-card p-4 space-y-3 max-h-[600px] overflow-y-auto">
            <div className="text-sm font-medium">Inputs</div>
            <div className="grid grid-cols-2 gap-2">
              {Object.keys(ratioIn).map((k) => (
                <div key={k}>
                  <label className="block text-[10px] uppercase tracking-wider text-muted-foreground mb-1">{k.replace(/_/g, " ")}</label>
                  <input type="number" value={ratioIn[k]} onChange={(e) => setRatioIn({ ...ratioIn, [k]: parseFloat(e.target.value) || 0 })}
                    data-testid={`ratio-${k}`}
                    className="h-8 w-full rounded-sm border border-border bg-background px-2 text-sm font-mono" />
                </div>
              ))}
            </div>
            <button onClick={runRatios} disabled={busy} data-testid="ratios-run"
              className="w-full h-9 text-sm rounded-sm text-white disabled:opacity-60"
              style={{ background: accent }}>
              {busy ? "Computing…" : "Compute ratios"}
            </button>
          </div>
          <div className="rounded-sm border border-border/60 bg-card p-4">
            <div className="text-sm font-medium mb-3">Ratio dashboard</div>
            {ratioOut ? (
              <div className="grid grid-cols-2 gap-3">
                {Object.entries(ratioOut).map(([k, v]) => (
                  <div key={k} className="rounded-sm border border-border/40 bg-secondary/30 px-3 py-2" data-testid={`ratio-out-${k}`}>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{k.replace(/_/g, " ")}</div>
                    <div className="font-mono text-sm mt-1">{v == null ? "—" : (typeof v === "number" && Math.abs(v) < 1 ? (v * 100).toFixed(2) + "%" : v.toFixed(2))}</div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-sm text-muted-foreground">Enter financials and press Compute.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
